import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import InputPassword from 'components/form/InputPassword';
import InputField from 'components/form/InputField';
import styled from 'styled-components';
import { theme } from 'utils/style/theme';
import logoIcon from 'utils/icon/carpe.png';
import PrimaryButton from 'components/button/PrimaryButton';
import ConfirmPasswordInput from 'components/form/InputPassword/ConfirmPasswordInput';
import { Link, useNavigate } from 'react-router-dom';
import { userApi } from 'apis/user/userApi';
import { ToastContainer, toast } from 'react-toastify';

const CreateOrganization: React.FC = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({});

  const [loading, setLoading] = useState<boolean>(false);
  const onSubmit: SubmitHandler<any> = async (data) => {
    setLoading(true);
    const response = await userApi.init(
      data.organization,
      data.email,
      data.password,
      data.passwordConfirm,
    );
    if (response.isSuccess) {
      toast('Create account success!');
      navigate(`/login`);
    } else {
      toast(response.error);
    }
    setLoading(false);
  };

  return (
    <>
      <PageWrapper>
        <LoginWrapper>
          <LogoWrapper>
            <img src={logoIcon} alt='logo' className='logo' />
          </LogoWrapper>
          <FormWrapper>
            <TitleWrapper>
              <h1>Create Organization</h1>
            </TitleWrapper>
            <form onSubmit={handleSubmit(onSubmit)} className='space-y-[12px]'>
              <InputWrapper>
                <InputField
                  register={register}
                  errors={errors}
                  title={`Organization`}
                  name='organization'
                  type='string'
                  placeHolder='Enter your organization'
                  errText='This field is required'
                  opts={{
                    required: 'This field is required',
                    maxLength: {
                      value: 100,
                      message: 'Organization address is too long',
                    },
                    minLength: {
                      value: 1,
                      message: 'Organization address is too short',
                    },
                  }}
                />
                <InputField
                  register={register}
                  errors={errors}
                  title={`Admin Email`}
                  name='email'
                  type='string'
                  placeHolder='Enter your email'
                  errText='This field is required'
                  opts={{
                    required: 'This field is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                    maxLength: {
                      value: 100,
                      message: 'Email address is too long',
                    },
                    minLength: {
                      value: 10,
                      message: 'Email address is too short',
                    },
                  }}
                />
                <InputPassword
                  register={register}
                  errors={errors}
                  title={`Password`}
                  name='password'
                  type='string'
                  placeHolder='Enter your password'
                  errText='This field is required'
                  opts={{
                    required: 'This field is required',
                    maxLength: {
                      value: 100,
                      message: '',
                    },
                    minLength: {
                      value: 2,
                      message: '',
                    },
                  }}
                />
                <ConfirmPasswordInput
                  register={register}
                  watch={watch}
                  errors={errors}
                  title='Password confirm'
                  name='passwordConfirm'
                  type='string'
                  placeHolder='Enter your password'
                  errText='This field is required'
                  opts={{
                    required: 'This field is required',
                    maxLength: {
                      value: 100,
                      message: '',
                    },
                    minLength: {
                      value: 2,
                      message: '',
                    },
                  }}
                />
              </InputWrapper>
              <FooterFormWrapper>
                <button className='submit-btn'>
                  <PrimaryButton
                    text='Create'
                    height='40px'
                    width='100%'
                    bgColor={theme.colors.blue_500}
                    textColor='#ffffff'
                    fontWeight={theme.fontWeights.medium}
                    fontSize={theme.fontSizes.normal}
                    border={`1px solid ${theme.colors.blue_500}`}
                    rounded='6px'
                    hoverBgColor={theme.colors.blue_600}
                    activeBgColor={theme.colors.blue_800}
                    selectedBgColor={theme.colors.blue_700}
                    loading={loading}
                  />
                </button>
              </FooterFormWrapper>
            </form>
          </FormWrapper>
        </LoginWrapper>
      </PageWrapper>
      <ToastContainer />
    </>
  );
};

export default CreateOrganization;

const PageWrapper = styled.div`
  background-color: #fbfbfb;
  width: 100%;
  height: 100vh;
  a {
    text-decoration: none;
  }
`;

const LoginWrapper = styled.div`
  margin: 0 auto;
  width: 80%;
  @media (min-width: 1440px) {
    width: 40%;
  }
`;

const FormWrapper = styled.div`
  background: #ffffff;
  border: 1px solid ${theme.colors.gray_8};
  border-radius: 32px;
  padding: 48px;
`;

const TitleWrapper = styled.div`
  margin-bottom: 16px;
  text-align: center;
  h1 {
    font-size: ${theme.fontSizes.xxl};
    color: ${theme.colors.dark_500};
    font-weight: ${theme.fontWeights.medium};
  }
  h2 {
    font-size: ${theme.fontSizes.normal};
    color: ${theme.colors.dark_200};
    font-weight: ${theme.fontWeights.normal};
    line-height: 24px;
    letter-spacing: 0.2px;
  }
`;

const InputWrapper = styled.div`
  width: 60%;
  margin: 0 auto;
  & > * + * {
    margin-top: 24px;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 40px;
  margin-bottom: 26px;
  .logo {
    width: 157px;
    height: 102px;
  }
`;

const FooterFormWrapper = styled.div`
  width: 60%;
  margin: 0 auto;
  margin-top: 32px;
  & > * + * {
    margin-top: 12px;
  }
  .submit-btn {
    width: 100%;
  }
  .forget-text {
    font-size: ${theme.fontSizes.normal};
    font-weight: ${theme.fontWeights.medium};
    line-height: 24px; /* 171.429% */
    letter-spacing: 0.2px;
    color: ${theme.colors.dark_200};
    text-align: center;
    cursor: pointer;
  }
`;
